import { useState } from 'react'
import { useLocalStorage } from 'src/shared/hooks/useStorage'
import { Card, CardTitle } from 'src/shared/components/ui'
import { CardContent } from 'src/shared/components/ui/card'
import { Button } from 'src/shared/components/ui'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { LogOut, Trash2 } from 'lucide-react'
import { useToast } from 'src/shared/hooks/use-toast'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { useSupabaseClient } from 'src/api/supabase'
import { useAuth } from 'src/shared/hooks/authProvider'
import { Badge } from 'src/shared/components/ui/badge'
import { useOrgUsersApi } from 'src/api/db/apiOrgUsers'
import { Checkbox } from 'src/shared/components/ui/checkbox'
import { TEST_NOTIFICATIONS_STORAGE_KEY } from 'src/shared/consts/testNotifications'
import { useIsAdmin } from 'src/shared/hooks/useIsAdmin'

export const DangerZone = () => {
  const { t } = useLocalization()
  const { toast } = useToast()
  const supabase = useSupabaseClient()
  const [loggingOut, setLoggingOut] = useState(false)
  const { session } = useAuth()
  const { orgUser } = useOrgUsersApi()
  const [testNotifications, setTestNotifications] = useLocalStorage(
    TEST_NOTIFICATIONS_STORAGE_KEY,
    false
  )
  const isAdmin = useIsAdmin()

  const handleLogout = async () => {
    setLoggingOut(true)
    try {
      const { error } = await supabase.auth.signOut()
      if (error) throw error
    } catch (error) {
      console.error('Logout error:', error)
      toast({
        variant: 'destructive',
        title: t('Could not log out', 'Kunde inte logga ut'),
        description: t('Please try again.', 'Försök igen.'),
      })
    } finally {
      setLoggingOut(false)
    }
  }

  return (
    <Card>
      <CardContent className="p-6">
        <Column className="gap-6">
          <CardTitle className="text-lg">
            {t('Account & security', 'Konto & säkerhet')}
          </CardTitle>

          <Row className="items-center justify-between p-4 rounded-lg border border-border bg-muted/30">
            <Column className="gap-0.5">
              <p className="text-sm font-medium text-foreground">
                {t('Log out', 'Logga ut')}
              </p>

              <p className="text-xs text-muted-foreground">
                {t(
                  'Sign out of your account on this device.',
                  'Logga ut från ditt konto på den här enheten.'
                )}
              </p>
            </Column>

            <Button size="sm" disabled={loggingOut} onClick={handleLogout}>
              <LogOut className="h-4 w-4 mr-2" />
              {t('Log out', 'Logga ut')}
            </Button>
          </Row>

          <Row className="items-center justify-between p-4 rounded-lg border border-destructive/20 bg-destructive/5">
            <Column className="gap-0.5">
              <p className="text-sm font-medium text-foreground">
                {t('Deactivate workspace', 'Inaktivera workspace')}
              </p>

              <p className="text-xs text-muted-foreground">
                {t(
                  'This will temporarily disable access for all non-admin users. Data is preserved.',
                  'Detta kommer tillfälligt inaktivera åtkomst för alla icke-administratörer. Data bevaras.'
                )}
              </p>
            </Column>

            <Button
              variant="destructive"
              size="sm"
              disabled
              onClick={() => {
                // TODO: Implement deactivate workspace functionality
                toast({
                  variant: 'destructive',
                  title: t('Feature coming soon', 'Funktion kommer snart'),
                })
              }}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              {t('Deactivate workspace', 'Inaktivera workspace')}
            </Button>
          </Row>

          <Row className="gap-2">
            <Badge>Email: {session?.user?.email || 'unknown email'}</Badge>
            <Badge>User id: {session?.user?.id || 'unknown id'}</Badge>
            <Badge>
              Org id:{' '}
              {orgUser?.data?.organization_id || 'unknown organization id'}
            </Badge>
          </Row>

          {isAdmin && (
            <Checkbox
              checked={testNotifications}
              setChecked={(checked) => setTestNotifications(Boolean(checked))}
              label={t('Test notifications', 'Testa notifikationer')}
            />
          )}
        </Column>
      </CardContent>
    </Card>
  )
}
